import { DestroyRef, Directive, ElementRef, OnDestroy, OnInit, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';

/**
 * Reports whether the host element is intersecting the viewport.
 *
 * The element counts as intersecting once half of it is visible
 * (threshold 0.5). State changes are debounced so rapid enter/leave
 * flapping while the user scrolls produces a single report.
 */
@Directive({
  selector: '[appDomIntersectionObserver]',
})
export class DomIntersectionObserverDirective implements OnInit, OnDestroy {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * How long an intersection state must hold before it is reported, in milliseconds.
   */
  readonly debounceTime = input(250);

  /**
   * When true, the element is observed for its whole lifetime.
   * When false, observation stops after the element first becomes visible.
   */
  readonly isContinuous = input(false);

  /**
   * Emits whether the element is intersecting the viewport.
   */
  readonly isIntersecting = output<boolean>();

  /**
   * The observer watching the host element.
   */
  private observer?: IntersectionObserver;

  /**
   * Lifecycle
   */
  ngOnInit() {
    const intersections = new Subject<boolean>();

    this.observer = new IntersectionObserver(
      // the callback can batch several threshold crossings; the newest wins
      (entries) => intersections.next(entries[entries.length - 1].isIntersecting),
      { threshold: 0.5 },
    );

    intersections.pipe(debounceTime(this.debounceTime()), takeUntilDestroyed(this.destroyRef)).subscribe((intersecting) => {
      // one-shot mode: stop watching once the element has become visible
      if (intersecting && !this.isContinuous()) {
        this.observer?.disconnect();
      }

      this.isIntersecting.emit(intersecting);
    });

    this.observer.observe(this.element.nativeElement);
  }

  /**
   * Lifecycle
   */
  ngOnDestroy() {
    this.observer?.disconnect();
  }
}
